import { Star, ShieldCheck, CalendarDays } from "lucide-react";
import { AppStoreButton } from "@/components/AppStoreButton";

const bills = [
  { day: "Jul 08", name: "Rent", amount: "-$1,450" },
  { day: "Jul 12", name: "Credit Card", amount: "-$310" },
  { day: "Jul 19", name: "Car Insurance", amount: "-$128" },
];

export const HeroSection = () => (
  <section
    data-testid="hero-section"
    className="relative overflow-hidden pt-32 pb-16 md:pt-40 md:pb-24 px-4 sm:px-6 lg:px-8"
  >
    <div
      className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full opacity-25 blur-3xl"
      style={{ background: "radial-gradient(closest-side, #38BDF8, transparent)" }}
    />
    <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 lg:grid-cols-12">
      <div className="rise-in flex flex-col items-start gap-6 text-left lg:col-span-7">
        <span
          data-testid="hero-eyebrow"
          className="font-mono-num text-xs font-semibold uppercase tracking-[0.2em] text-sky-400"
        >
          iOS Exclusive • Personal Payment Planner
        </span>
        <h1 className="text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
          Know exactly what's{" "}
          <span className="text-sky-400">safe to spend.</span>
          <br />
          Before every bill lands.
        </h1>
        <p className="max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
          Stop guessing between paychecks. Payment Pulse maps out your upcoming
          30 days, guards a 3-day safety buffer, and calculates your true,
          stress-free disposable cash.
        </p>
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <AppStoreButton testId="hero-appstore-button" sublabel="Free on iOS" />
          <div
            data-testid="hero-rating-inline"
            className="flex items-center gap-2 text-sm text-slate-400"
          >
            <span className="flex text-amber-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-amber-400" />
              ))}
            </span>
            <span>
              <span className="font-semibold text-white">5.0</span> on the App
              Store
            </span>
          </div>
        </div>
        <p className="text-sm text-slate-500">
          Built for irregular income &amp; multiple bills — by Walter Paul
          Bebirian.
        </p>
      </div>

      <div className="rise-in flex items-center justify-center lg:col-span-5" style={{ animationDelay: "0.15s" }}>
        <div
          data-testid="hero-phone-mockup"
          className="relative w-[300px] rounded-[2.6rem] border border-white/15 bg-[#0D1527] p-3 shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
        >
          <div className="rounded-[2rem] bg-[#070C18] px-5 pb-7 pt-4">
            <div className="mx-auto mb-5 h-6 w-28 rounded-full bg-black/80" />
            <p className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500">
              Safe to spend today
            </p>
            <p className="font-mono-num mt-1 text-4xl font-semibold text-emerald-400">
              $342.50
            </p>
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-emerald-400/25 bg-emerald-400/10 px-3 py-2.5">
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
              <span className="text-xs text-emerald-300">
                3-day buffer protected: $450 reserved
              </span>
            </div>
            <div className="mt-5 space-y-2.5">
              <p className="flex items-center gap-1.5 font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500">
                <CalendarDays className="h-3 w-3" /> Next 30 days
              </p>
              {bills.map((b) => (
                <div
                  key={b.name}
                  className="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2"
                >
                  <span className="text-xs text-slate-300">
                    <span className="mr-2 font-mono-num text-slate-500">{b.day}</span>
                    {b.name}
                  </span>
                  <span className="font-mono-num text-xs text-slate-400">{b.amount}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-white/10">
              <div className="h-full w-2/3 rounded-full bg-gradient-to-r from-sky-500 to-emerald-400" />
            </div>
            <p className="mt-2 text-[10px] text-slate-500">
              Cash trajectory stays positive through Aug 05
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);
